import React, { useCallback } from 'react'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import { AgentPanelProvider, useAgentPanelContext } from './AgentPanelContext'
import ChatPanel from './chat/ChatPanel'

const useStyles = makeStyles(() => ({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: 'var(--padding-sm) var(--padding-md)',
    borderBottom: '1px solid var(--color-ai-200, rgba(169,88,255,0.18))',
    background: 'var(--color-ai-050)',
    flexShrink: 0,
    gap: '10px'
  },
  title: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 'var(--text-weight-bold)',
    color: 'var(--color-primary-700)',
    flexShrink: 0
  },
  headerSpacer: { flex: 1 },
  autoAcceptToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
    cursor: 'pointer',
    userSelect: 'none',
    padding: '3px 8px',
    borderRadius: '12px',
    border: '1px solid transparent',
    transition: 'background-color 0.2s ease, border-color 0.2s ease'
  },
  autoAcceptToggleOn: {
    backgroundColor: 'var(--color-ai-100, rgba(169,88,255,0.12))',
    borderColor: 'var(--color-ai-300, rgba(169,88,255,0.35))'
  },
  toggleTrack: {
    width: '28px',
    height: '16px',
    borderRadius: '8px',
    backgroundColor: 'var(--color-neutral-300)',
    position: 'relative',
    transition: 'background-color 0.2s ease',
    flexShrink: 0
  },
  toggleTrackOn: { backgroundColor: 'var(--color-ai-500)' },
  toggleThumb: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: 'white',
    position: 'absolute',
    top: '2px',
    left: '2px',
    transition: 'transform 0.2s ease',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
  },
  toggleThumbOn: { transform: 'translateX(12px)' },
  toggleLabel: {
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    color: 'var(--color-neutral-400)',
    whiteSpace: 'nowrap'
  },
  toggleLabelOn: { color: 'var(--color-ai-600)' }
}))

function DesignerHeader () {
  const classes = useStyles()
  const { autoAccept, setAutoAccept } = useAgentPanelContext()

  const handleToggle = useCallback(() => {
    setAutoAccept(prev => !prev)
  }, [setAutoAccept])

  return (
    <div className={classes.header}>
      <span className={classes.title}>Blueprint Designer</span>
      <div className={classes.headerSpacer} />
      <div
        className={`${classes.autoAcceptToggle} ${autoAccept ? classes.autoAcceptToggleOn : ''}`}
        onClick={handleToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle() } }}
        role='switch'
        aria-checked={autoAccept}
        tabIndex={0}
        title={autoAccept ? 'Auto-apply is ON: proposals are merged into the draft automatically' : 'Auto-apply is off: each proposal must be reviewed manually'}
      >
        <span className={`${classes.toggleLabel} ${autoAccept ? classes.toggleLabelOn : ''}`}>
          {autoAccept ? 'Auto-apply ON' : 'Auto-apply'}
        </span>
        <div className={`${classes.toggleTrack} ${autoAccept ? classes.toggleTrackOn : ''}`}>
          <div className={`${classes.toggleThumb} ${autoAccept ? classes.toggleThumbOn : ''}`} />
        </div>
      </div>
    </div>
  )
}

function AgentSidePanel ({ workspaceContext, onApplyProposal, appliedProposalIds }) {
  const classes = useStyles()
  return (
    <AgentPanelProvider
      agentId='blueprint-designer'
      workspaceContext={workspaceContext}
      onApplyProposal={onApplyProposal}
      appliedProposalIds={appliedProposalIds}
    >
      <div data-agent-panel className={classes.panel}>
        <DesignerHeader />
        <ChatPanel />
      </div>
    </AgentPanelProvider>
  )
}

AgentSidePanel.propTypes = {
  workspaceContext: PropTypes.object,
  onApplyProposal: PropTypes.func,
  appliedProposalIds: PropTypes.instanceOf(Set)
}

export default AgentSidePanel
